import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ClipboardCheck, ThumbsUp, ThumbsDown, Loader2, ChevronDown, ChevronRight } from 'lucide-react'
import { apiGet, postJson } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import { asList } from '../../lib/datetime.js'
import { Section } from '../ui/DrawerPrimitives.jsx'
import { Badge, Button, ConfirmDialog, ErrorState, InlineError, Input, Loading, fmtDate, fmtTime, useToast } from '../../kit'
import { reviewState, reviewMeta, lastReviewId, roundStartMs, STEP_LABEL, RESULT_LABEL } from '../../lib/implantReview.js'
import { parkReasonLabel } from '../../lib/implant.js'
import { OUTCOME_BY_CODE } from '../../data/implantOutcomes.js'

/* รีวิวก่อนโทร — steps 1 and 2 of Ram's implant process, inside the recall popup on
 * /reports/implant. Step 3 is the call, which the โน้ต Recall section below already is.
 *
 * Anyone may press any button (Ram: "everyone can do the step 1-2-3"). Each press is
 * one row in Xano implant_review; the reviewer comes from the login, not from here.
 * The rule for what the rows mean lives in src/lib/implantReview.js and is the same
 * one api 650 uses for the list column — see that file before changing anything.
 *
 * 🔑 NOT A RECALL NOTE. Writing the review as a note would fire trigger 7, reset
 * last_recall, and drop the patient out of list B / under "เพิ่งโทรไป" before anyone
 * called. That is the reason this is a separate table and a separate section.
 *
 * The button for the answer a step already has is pressed-in and does nothing: a
 * second 👍 on step 1 would be a re-screen, and a re-screen sends a พร้อมโทร patient
 * back to รอตรวจซ้ำ. Changing an answer, or ล้าง, is always one deliberate click.
 *
 * 🔑 STALE POPUPS ARE REFUSED BY XANO (pre-deploy review, 2026-09-17). Every click
 * sends `expected_last_id` — the newest review id this popup knows of — and api 658
 * answers 400 if a colleague has reviewed the patient since. The catch below shows
 * that sentence and re-reads the history, so the next click is made on what is true.
 * `ownIdRef` covers the gap between our own save and the re-read landing.
 *
 * 2026-10-29 (implant/report.md §6g, phase 5). `row` is the api 678 list row:
 *   • reviews belong to a ROUND — only rows since row.round_start decide the status,
 *     older ones stay in the history, the way api 678 counts them
 *   • บันทึกของผู้รีวิว — a note that saves on its own (step 0, result note), so a
 *     reviewer never needs the recall box, where a note counts as a call
 *   • ดึงกลับมาโทรตอนนี้ — for a parked patient (row.park_kind P / R): one step-0
 *     'reopen' row with a reason; api 678 then lists them as พร้อมโทร again */
export default function ImplantReviewSection({ patientId, list = '', row = null, onChanged, refreshKey = 0 }) {
  const [rows, setRows] = useState(null)       // null = loading, or never loaded
  const [err, setErr] = useState('')           // a save that failed
  const [loadErr, setLoadErr] = useState('')   // the history could not be read
  const [busy, setBusy] = useState('')          // `${step}:${result}` while saving
  const [reason, setReason] = useState('')
  const [noteText, setNoteText] = useState('')       // บันทึกของผู้รีวิว
  const [reopenText, setReopenText] = useState('')   // why bring a parked patient back
  const [confirmClear, setConfirmClear] = useState(null)   // step awaiting ล้าง confirm
  const [showHistory, setShowHistory] = useState(false)
  const ownIdRef = useRef(0)   // id of this popup's last saved review, until a re-read has it
  const toast = useToast()

  const load = useCallback(() => {
    if (patientId == null) return
    apiGet('/api/implant/review', { patient_id: patientId })
      .then((r) => { setRows(asList(r?.rows ?? r)); setLoadErr('') })
      // Never fall back to an empty history: that would read as ยังไม่รีวิว, with
      // live buttons, for a patient someone may already have failed.
      .catch((e) => setLoadErr(errText(e, 'โหลดผลรีวิวไม่สำเร็จ')))
  }, [patientId])
  useEffect(() => { ownIdRef.current = 0; setRows(null); setErr(''); setLoadErr(''); setReason(''); setNoteText(''); setReopenText(''); setConfirmClear(null); setShowHistory(false); load() }, [patientId, load])
  // A colleague's action on this patient bumps the parent's refreshKey.
  useEffect(() => { if (refreshKey) load() }, [refreshKey, load])

  // step 0 rows (note / reopen) carry their own text; review steps carry `reason`.
  async function save(step, result, text = reason) {
    if (busy) return
    setBusy(`${step}:${result}`)
    setErr('')
    try {
      const res = await postJson('/api/implant/review', {
        patient_id: patientId, step, result,
        reason: result === 'clear' ? '' : String(text || '').trim(),
        list,
        expected_last_id: Math.max(lastReviewId(rows), ownIdRef.current),
        // the round the status above is counted in — api 658 checks repeats inside it
        ...(sinceMs ? { since_ms: sinceMs } : {}),
      })
      ownIdRef.current = Math.max(ownIdRef.current, Number(res?.id) || 0)
      if (result === 'note') setNoteText('')
      else if (result === 'reopen') setReopenText('')
      else if (result !== 'clear') setReason('')
      setConfirmClear(null)
      toast.success(result === 'clear' ? 'ล้างผลแล้ว' : result === 'note' ? 'บันทึกแล้ว'
        : result === 'reopen' ? 'ดึงกลับมาโทรแล้ว — คนไข้กลับเข้าลิสต์เป็นพร้อมโทร' : 'บันทึกผลรีวิวแล้ว')
      load()
      onChanged?.()
    } catch (e) {
      setConfirmClear(null)
      setErr(errText(e, 'บันทึกไม่สำเร็จ'))
      toast.error('บันทึกไม่สำเร็จ')
      load()   // someone else may have changed step 1 underneath this popup
    } finally {
      setBusy('')
    }
  }

  const sinceMs = roundStartMs(row?.round_start)
  const st = reviewState(rows || [], { sinceMs })
  // W starts at พร้อมโทร with no review (api 678): say so instead of "ยังไม่รีวิว".
  const warmReady = row?.list === 'W' && st.status === 'none'
  const meta = reviewMeta(warmReady ? 'ready' : st.status)
  const olderRows = sinceMs ? (rows || []).filter((r) => Number(r?.step) > 0 && (Number(r?.created_at) || 0) < sinceMs).length : 0
  const parked = row?.park_kind === 'P' || row?.park_kind === 'R'

  return (
    <Section title="รีวิวก่อนโทร (รากเทียม)" icon={ClipboardCheck}>
      {rows == null ? (loadErr ? (
        <ErrorState title="โหลดผลรีวิวไม่สำเร็จ" message={loadErr} onRetry={load} />
      ) : <Loading variant="inline" />) : (
        <div className="rounded-lg border border-slate-100 bg-slate-50/50 px-3 py-2.5 space-y-2.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ${meta.pill}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />{meta.label}
            </span>
            <span className="text-[11.5px] text-slate-500">{warmReady ? 'Warm Case เริ่มที่พร้อมโทร ไม่ต้องรีวิว' : meta.hint}</span>
          </div>
          {sinceMs > 0 && olderRows > 0 && (
            <p className="text-[11px] text-slate-400">
              รอบนี้เริ่ม {fmtDate(sinceMs)} (ถอนฟันหรือปรึกษาครั้งล่าสุด) · รีวิว {olderRows} ครั้งก่อนหน้านั้นเป็นประวัติ ไม่นับแล้ว
            </p>
          )}

          {/* A parked patient (api 678 views P / R): why, until when, and the way back.
              พักไว้ (6 months) is amber; พักจนกว่าจะมีรอบใหม่ is sky, the colour of the
              NO / OC chips that put a patient there (kit Badge `color`: no sky tone). */}
          {parked && (
            <div className={`rounded-md border px-2.5 py-2 space-y-1.5 ${row.park_kind === 'R' ? 'border-sky-200 bg-sky-50/60' : 'border-amber-200 bg-amber-50/60'}`}>
              <div className={`flex items-center gap-2 flex-wrap text-[12px] ${row.park_kind === 'R' ? 'text-sky-800' : 'text-amber-800'}`}>
                <Badge tone="amber" color={row.park_kind === 'R' ? '#0284c7' : undefined}>{row.park_kind === 'P' ? 'พักไว้' : 'พักจนกว่าจะมีรอบใหม่'}</Badge>
                <span>{parkReasonLabel(row.park_reason, OUTCOME_BY_CODE)}</span>
                {row.park_kind === 'P' && row.park_until && <span>· กลับเข้าลิสต์เอง {fmtDate(row.park_until)}</span>}
                {row.park_kind === 'R' && <span>· กลับมาเมื่อถอนฟันซี่ใหม่หรือมาปรึกษาใหม่</span>}
              </div>
              <div className="flex items-center gap-2">
                <Input value={reopenText} onChange={(e) => setReopenText(e.target.value.slice(0, 300))} maxLength={300}
                  placeholder="เหตุผลที่ดึงกลับมา เช่น มีโปรใหม่ · กดผิด" aria-label="เหตุผลที่ดึงกลับมาโทร" />
                <Button size="sm" variant="secondary" loading={busy === '0:reopen'} disabled={!reopenText.trim() || !!busy}
                  disabledReason={!reopenText.trim() ? 'พิมพ์เหตุผลก่อน' : undefined}
                  onClick={() => save(0, 'reopen', reopenText)}>ดึงกลับมาโทรตอนนี้</Button>
              </div>
            </div>
          )}

          <StepRow
            step={1} answer={st.step1} busy={busy}
            onPick={(r) => save(1, r)}
            confirming={confirmClear === 1} onAskClear={() => setConfirmClear(1)} onCancelClear={() => setConfirmClear(null)}
            note={st.step2 ? 'เปลี่ยนผลคัดกรอง = ต้องตรวจซ้ำใหม่' : ''}
          />
          <StepRow
            step={2} answer={st.step2} busy={busy} disabled={!st.canStep2}
            onPick={(r) => save(2, r)}
            confirming={confirmClear === 2} onAskClear={() => setConfirmClear(2)} onCancelClear={() => setConfirmClear(null)}
            note={!st.canStep2
              ? 'ต้องผ่านขั้นคัดกรองก่อน'
              : st.staleStep2 ? `ตรวจซ้ำครั้งก่อน (${RESULT_LABEL[st.staleStep2.result]} · ${fmtDate(st.staleStep2.created_at)}) ไม่นับแล้ว เพราะมีการคัดกรองใหม่` : ''}
          />

          <Input value={reason} onChange={(e) => setReason(e.target.value.slice(0, 300))} maxLength={300}
            placeholder="เหตุผล (ถ้ามี) — บันทึกไปกับปุ่มที่กดถัดไป" aria-label="เหตุผล" />

          {/* A reviewer's note that saves on its own — never type it in โน้ต Recall below,
              where any note counts as a call and resets "เพิ่งโทรไป". */}
          <div className="flex items-center gap-2">
            <Input value={noteText} onChange={(e) => setNoteText(e.target.value.slice(0, 300))} maxLength={300}
              placeholder="บันทึกของผู้รีวิว เช่น ซี่ 25 · เคยถอนที่อื่น — บันทึกได้ทุกเมื่อ" aria-label="บันทึกของผู้รีวิว" />
            <Button size="sm" variant="secondary" loading={busy === '0:note'} disabled={!noteText.trim() || !!busy}
              onClick={() => save(0, 'note', noteText)}>บันทึก</Button>
          </div>

          <InlineError>{err}</InlineError>
          {loadErr && <InlineError>อ่านผลล่าสุดไม่สำเร็จ ผลที่เห็นอาจไม่ใช่ล่าสุด: {loadErr}</InlineError>}
          <p className="text-[11px] text-slate-400">
            ใครก็รีวิวได้ทั้ง 2 ขั้น · การรีวิวไม่นับเป็นการโทร — ไม่ย้ายคนไข้ออกจากลิสต์ B · ผลการโทรบันทึกที่ “โน้ต Recall” ในแท็บรอโทร
          </p>

          {rows.length > 0 && (
            <div>
              <button type="button" onClick={() => setShowHistory((v) => !v)}
                className="inline-flex items-center gap-1 text-[11.5px] text-slate-500 hover:text-slate-700">
                {showHistory ? <ChevronDown size={12} /> : <ChevronRight size={12} />} ประวัติการรีวิว ({rows.length})
              </button>
              {showHistory && (
                <ul className="mt-1.5 space-y-1">
                  {[...rows].sort((a, b) => Number(b.id) - Number(a.id)).map((r) => (
                    <li key={r.id} className="text-[11.5px] text-slate-600">
                      <span className="tabular-nums text-slate-400">{fmtDate(r.created_at)} {fmtTime(r.created_at)}</span>
                      {r.by_code && <span className="text-slate-500"> · {r.by_code}</span>}
                      <span> · {Number(r.step) === 0 ? (RESULT_LABEL[r.result] || r.result) : `${STEP_LABEL[r.step] || `ขั้น ${r.step}`} ${RESULT_LABEL[r.result] || r.result}`}</span>
                      {sinceMs > 0 && Number(r.step) > 0 && (Number(r.created_at) || 0) < sinceMs && <span className="text-slate-400"> · รอบก่อน</span>}
                      {r.list && <span className="text-slate-400"> · ลิสต์ {r.list}</span>}
                      {r.reason && <span className="text-slate-500"> — {r.reason}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
      {/* Portalled: this section sits inside a Drawer, whose panel would otherwise
          confine a fixed overlay to its own width. */}
      {confirmClear != null && createPortal(
        <ConfirmDialog
          title={`ล้างผล${STEP_LABEL[confirmClear] || `ขั้น ${confirmClear}`}?`}
          message={`ผล${STEP_LABEL[confirmClear] || `ขั้น ${confirmClear}`}ของคนไข้รายนี้จะถูกล้าง${confirmClear === 1 && st.step2 ? ' · เปลี่ยนผลคัดกรอง = ต้องตรวจซ้ำใหม่' : ''}`}
          confirmLabel="ล้างผล"
          busy={!!busy}
          onConfirm={() => save(confirmClear, 'clear')}
          onCancel={() => setConfirmClear(null)}
        />,
        document.body,
      )}
    </Section>
  )
}

// Xano prefixes a precondition's sentence with "Param:  - " on some paths.
const errText = (e, fallback) => apiErrorText(e, fallback).replace(/^Param:\s*-\s*/, '')

// `onCancelClear` is kept for the call sites' shape; the dialog's ยกเลิก closes it now.
// eslint-disable-next-line no-unused-vars
export function StepRow({ step, answer, busy, disabled = false, onPick, confirming, onAskClear, onCancelClear, note }) {
  const cur = answer?.result || null
  const btn = (result, Icon, label, onCls) => {
    const on = cur === result
    const saving = busy === `${step}:${result}`
    return (
      <button type="button" aria-pressed={on} disabled={disabled || !!busy || on}
        onClick={() => onPick(result)}
        title={on ? 'ผลปัจจุบันของขั้นนี้' : `${STEP_LABEL[step]}: ${label}`}
        className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[12px] font-medium transition disabled:cursor-not-allowed
          ${on ? onCls : 'border-slate-200 bg-white text-slate-600 enabled:hover:border-slate-300 disabled:opacity-40'}`}>
        {saving ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />}{label}
      </button>
    )
  }
  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="w-[74px] shrink-0 text-[12px] font-medium text-slate-700">{step} · {STEP_LABEL[step]}</span>
        {btn('pass', ThumbsUp, 'ผ่าน', 'border-emerald-300 bg-emerald-50 text-emerald-700')}
        {btn('fail', ThumbsDown, 'ไม่ผ่าน', 'border-rose-300 bg-rose-50 text-rose-700')}
        {/* ล้าง asks through a ConfirmDialog (owned by the section) — `confirming`
            keeps the button pressed-looking while it is open. */}
        {answer && (
          <button type="button" onClick={onAskClear} disabled={!!busy || confirming}
            className="text-[11.5px] text-slate-400 hover:text-slate-600 disabled:opacity-40">ล้าง</button>
        )}
      </div>
      {answer && (
        <p className="mt-1 ml-[82px] text-[11.5px] text-slate-500">
          {RESULT_LABEL[answer.result]}{answer.by_code ? ` · ${answer.by_code}` : ''} · <span className="tabular-nums">{fmtDate(answer.created_at)} {fmtTime(answer.created_at)}</span>
          {answer.reason ? <span className="text-slate-600"> — {answer.reason}</span> : null}
        </p>
      )}
      {note && <p className="mt-0.5 ml-[82px] text-[11px] text-slate-400">{note}</p>}
    </div>
  )
}
